/**
 * app/components/HeaderBar.tsx
 * -----------------------------
 * What: Top app header that displays the current section title and optional
 * actions.
 * Why: Provides consistent navigation context across screens.
 */
// HeaderBar.tsx
//
// This file defines the HeaderBar component, a reusable header for the main app screens.
// It displays a title based on the current tab (Home, Rooms, Items, Settings).
// The title is determined by the 'titleKey' prop, which should match a key in the TITLES map.
//
// Usage:
//   - Used at the top of main tab screens (e.g., HomeTab, RoomsTab, ItemsTab, SettingsTab)
//   - Provides consistent theming and layout for screen headers
//   - Called from screen components to render the header bar
//
// The component uses the app's theme (from ThemeContext) for colors.
//
// Example:
//   <HeaderBar titleKey="ItemsTab" />

import React, { useEffect } from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { useTheme } from '../../lib/ThemeContext';

// Map of tab keys to display titles
export const TITLES: Record<string, string> = {
  HomeTab: 'Log My Home',
  RoomsTab: 'Rooms',
  ItemsTab: 'Items',
  SettingsTab: 'Settings',
};

// HeaderBar component
// Props:
//   - titleKey: string (should match a key in TITLES)
export default function HeaderBar({ titleKey }: { titleKey: string }) {
  const { theme } = useTheme(); // Get current theme colors
  return (
    // Header container with themed background and border
    <View style={[styles.header, { backgroundColor: theme.background, borderBottomColor: theme.border }]}> 
      {/* Title text, themed color, fallback to 'Log My Home' if key not found */}
      <Text style={[styles.title, { color: theme.success }]}>{TITLES[titleKey] || 'Log My Home'}</Text>
    </View>
  );
}

// Styles for the header and title
const styles = StyleSheet.create({
  header: {
    padding: 16, // Space around the title
    borderBottomWidth: 1, // Subtle bottom border
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
  },
}); 